import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import ScrollToTop from './motion/ScrollToTop';

// The public site was tuned on a 1920px screen at 150% Windows scaling:
// a 1280px-wide window rendered at `zoom: 0.9` (see .public-app in index.css),
// i.e. a 1280 / 0.9 ≈ 1422px-wide layout. On wider windows (e.g. the same
// screen at 100% scaling) we zoom up so the layout stays that wide and looks
// the same. Phones and tablets (< 1024px) stay at 100%.
//
// The zoom follows the *screen* width (changes with Windows display scaling)
// rather than the window width, because browser zoom (Ctrl +/-) changes the
// window width too and would otherwise be cancelled out. It is capped so the
// layout never gets narrower than the 1280px it was designed for, which only
// kicks in for narrow (non-maximised) windows or heavy Ctrl + zooming.
const BASE_ZOOM = 0.9;
const DESIGN_WIDTH = 1280 / BASE_ZOOM;
const MIN_LAYOUT_WIDTH = 1280;

function getPublicZoom() {
  const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
  if (viewportWidth < 1024) return 1;
  const screenZoom = window.screen.width / DESIGN_WIDTH;
  const maxZoom = viewportWidth / MIN_LAYOUT_WIDTH;
  return Math.max(BASE_ZOOM, Math.min(screenZoom, maxZoom));
}

export default function PublicLayout() {
  const [zoom, setZoom] = useState(getPublicZoom);

  useEffect(() => {
    const onResize = () => setZoom(getPublicZoom());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <div
      className="public-app flex flex-col bg-white"
      // Viewport units get zoomed too, so divide by the zoom to keep
      // "full screen height" meaning the real screen height.
      style={{ zoom, '--public-zoom': zoom, minHeight: `calc(100vh / ${zoom})` }}
    >
      <ScrollToTop />
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}
